/** One chat message; assistant replies can render as multiple natural bubbles. */
import { splitAssistantBubbles } from '../lib/chat';
import { extractSessionControlProposals } from '../lib/session-control-proposal';
import type { ChatTurn } from '../lib/types';
import { Markdown } from './Markdown';
import { SessionControlProposalCard } from './SessionControlProposalCard';

function AssistantAvatar() {
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-xs font-semibold text-white shadow-md shadow-indigo-950/40">
      L
    </span>
  );
}

function SelfAvatar() {
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-600 text-[11px] font-medium text-slate-100">
      我
    </span>
  );
}

export function ChatMessage({ turn, isLast }: { turn: ChatTurn; isLast: boolean }) {
  if (turn.role === 'user') {
    return (
      <div className="flex items-end justify-end gap-1.5">
        <div className="max-w-[82%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-indigo-500 px-3.5 py-2 text-sm leading-relaxed text-white shadow-md shadow-indigo-950/40">
          {turn.content}
        </div>
        <SelfAvatar />
      </div>
    );
  }

  if (turn.role === 'system') {
    return (
      <div className="flex justify-center">
        <div className="max-w-[92%] rounded-xl border border-emerald-400/20 bg-emerald-400/5 px-3 py-2 text-xs text-emerald-100/90">
          <Markdown text={turn.content} className="break-words leading-relaxed" />
        </div>
      </div>
    );
  }

  const streaming = turn.status === 'streaming' && isLast;
  const extracted = extractSessionControlProposals(turn.content);
  const bubbles = streaming
    ? extracted.text.length > 0
      ? [extracted.text]
      : []
    : splitAssistantBubbles(extracted.text);

  return (
    <div className="flex max-w-[92%] items-start gap-1.5">
      <AssistantAvatar />
      <div className="flex min-w-0 flex-col gap-1.5">
        {bubbles.length > 0 ? (
          bubbles.map((bubble, index) => (
            <div
              key={`${turn.id}-${index}`}
              className="rounded-2xl rounded-bl-md border border-white/10 bg-[#0f1629] px-3.5 py-3 text-sm shadow-md shadow-black/20"
            >
              <Markdown
                text={bubble}
                className={`break-words leading-relaxed text-slate-100 ${
                  streaming && index === bubbles.length - 1 ? 'streaming-cursor' : ''
                }`}
              />
            </div>
          ))
        ) : streaming ? (
          <div className="rounded-2xl rounded-bl-md border border-white/10 bg-[#0f1629] px-3.5 py-3 text-xs text-slate-400">
            对方正在输入<span className="animate-pulse">…</span>
          </div>
        ) : null}
        {extracted.proposals.map((proposal) => (
          <SessionControlProposalCard key={proposal.id} proposal={proposal} />
        ))}
        {turn.error ? <p className="px-1 text-xs text-rose-300">{turn.error}</p> : null}
        {turn.status === 'stopped' ? <p className="px-1 text-xs text-slate-400">已停止</p> : null}
      </div>
    </div>
  );
}
